import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Avatar,
} from '@mui/material';
import { motion } from 'framer-motion';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CellTowerIcon from '@mui/icons-material/CellTower';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const Projects = () => {
  const pillars = [
    {
      icon: <CellTowerIcon />,
      title: 'Telecom Infrastructure',
      desc: 'Building modern, reliable towers that power connectivity across the region.',
      color: '#e60108',
    },
    {
      icon: <RocketLaunchIcon />,
      title: 'Strategic Expansion',
      desc: 'Investing in the right locations to reach communities and businesses where it matters most.',
      color: '#191919',
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Long-Term Growth',
      desc: 'Laying a foundation for a scalable network that grows with the region.',
      color: '#e60108',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#f7f7f7' }}>

      {/* ================= HERO SECTION ================= */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '65vh' },
          minHeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/tower.jpg"
          alt="Major Projects"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />

        {/* Dark overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.75) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <Chip
              label="OUR MAJOR PROJECTS"
              sx={{
                bgcolor: '#e60108',
                color: '#fff',
                mb: 3,
                px: 1,
                height: 38,
                fontWeight: 700,
                letterSpacing: '1px',
                mx: 'auto',
                display: 'inline-flex',
              }}
            />

            <Typography
              variant="h1"
              sx={{
                color: '#fff',
                mb: 3,
                fontSize: {
                  xs: '2.7rem',
                  sm: '3.5rem',
                  md: '5rem',
                },
                fontWeight: 800,
                lineHeight: 1.1,
                textShadow: '0 4px 25px rgba(0,0,0,0.4)',
              }}
            >
              Building Infrastructure
              <br />
              <Box component="span" sx={{ color: '#e60108' }}>
                For Tomorrow
              </Box>
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 780,
                mx: 'auto',
                fontSize: {
                  xs: '1.05rem',
                  md: '1.3rem',
                },
                lineHeight: 1.7,
              }}
            >
              Investing in strategic infrastructure and communication
              networks that connect communities, businesses and opportunities
              across Eswatini and beyond.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* ================= INTRODUCTION ================= */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography
                sx={{
                  color: '#e60108',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  mb: 2,
                }}
              >
                Our Vision
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  color: '#151515',
                  mb: 3,
                  fontSize: {
                    xs: '2rem',
                    md: '2.8rem',
                  },
                  lineHeight: 1.2,
                }}
              >
                Developing the infrastructure that powers connectivity
              </Typography>

              <Typography
                sx={{
                  color: '#555',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 2.5,
                }}
              >
                Our major projects represent our commitment to building
                reliable, modern infrastructure that supports the growing
                communication needs of communities and businesses.
              </Typography>

              <Typography
                sx={{
                  color: '#555',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                }}
              >
                From telecommunications infrastructure to strategically
                located tower sites, we are creating a foundation for
                stronger and more accessible connectivity.
              </Typography>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box
                sx={{
                  background: '#151515',
                  borderRadius: 4,
                  p: { xs: 4, md: 5 },
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: { md: 340 },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    bgcolor: 'rgba(230,1,8,0.15)',
                    right: -60,
                    top: -60,
                    pointerEvents: 'none',
                  }}
                />

                <Typography
                  variant="h4"
                  sx={{
                    color: '#fff !important',
                    fontWeight: 800,
                    mb: 2,
                    position: 'relative',
                    fontSize: { xs: '1.6rem', md: '1.9rem' },
                  }}
                >
                  From Vision to Reality
                </Typography>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.8) !important',
                    lineHeight: 1.8,
                    position: 'relative',
                    fontSize: '1.05rem',
                  }}
                >
                  We are moving beyond planning and actively investing in
                  physical infrastructure. Our first tower project marks an
                  important milestone in our journey.
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* ================= PILLARS ================= */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="WHAT WE BUILD"
              sx={{
                bgcolor: '#e6010810',
                color: '#e60108',
                mb: 2,
                fontWeight: 700,
                letterSpacing: '1px',
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: '#151515',
                mb: 2,
                fontSize: { xs: '2rem', md: '2.8rem' },
              }}
            >
              Three Pillars of Our Projects
            </Typography>
            <Typography
              sx={{
                color: '#a0a0a0',
                maxWidth: 700,
                mx: 'auto',
                mb: 6,
                fontSize: '1.1rem',
              }}
            >
              Every project we undertake is guided by the same principles
            </Typography>
          </motion.div>

          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {pillars.map((pillar, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      p: 4,
                      borderRadius: 4,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      border: '1px solid #eee',
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                        borderColor: pillar.color + '40',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: pillar.color + '15',
                        color: pillar.color,
                        mb: 3,
                        mx: 'auto',
                      }}
                    >
                      {pillar.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="800"
                      sx={{
                        color: '#151515',
                        textAlign: 'center',
                        fontSize: '1.2rem',
                      }}
                    >
                      {pillar.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#777',
                        textAlign: 'center',
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      {pillar.desc}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================= FEATURED PROJECT ================= */}
      <Box sx={{ bgcolor: '#f7f7f7', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">

          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="FEATURED PROJECT"
              sx={{
                bgcolor: '#e60108',
                color: '#fff',
                mb: 2,
                fontWeight: 700,
                letterSpacing: '1px',
                mx: 'auto',
                display: 'table',
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: '#151515',
                fontSize: {
                  xs: '2.2rem',
                  md: '3.2rem',
                },
              }}
            >
              Logoba Tower
            </Typography>

            <Typography
              sx={{
                color: '#666',
                maxWidth: 700,
                mx: 'auto',
                mt: 2,
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              Our first tower development is underway in Logoba, marking a
              major step forward in our infrastructure expansion.
            </Typography>
          </Box>

          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid #eee',
              bgcolor: '#fff',
              maxWidth: 860,
              mx: 'auto',
            }}
          >
            <Grid container>

              {/* Project Image */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: { xs: 220, md: '100%' },
                    minHeight: { md: 360 },
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: '#eee',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/tower.jpg"
                    alt="Logoba Tower Project"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.65), transparent 60%)',
                    }}
                  />

                  <Chip
                    label="IN DEVELOPMENT"
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      bgcolor: '#e60108',
                      color: '#fff',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              </Grid>

              {/* Project Details */}
              <Grid item xs={12} md={8}>
                <CardContent
                  sx={{
                    p: { xs: 4, md: 5 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: '#151515',
                        mb: 2,
                        fontSize: {
                          xs: '1.7rem',
                          md: '2rem',
                        },
                      }}
                    >
                      Our First Tower
                    </Typography>

                    <Typography
                      sx={{
                        color: '#555',
                        fontSize: '1rem',
                        lineHeight: 1.8,
                        mb: 3.5,
                      }}
                    >
                      We are proud to announce that we have secured land in
                      Logoba for the construction of our first telecommunications
                      tower.
                    </Typography>

                    {/* Project Status */}
                    <Stack spacing={2} sx={{ mb: 3.5 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            bgcolor: '#e6010815',
                            color: '#e60108',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#151515',
                              fontSize: '0.95rem',
                            }}
                          >
                            Land Secured
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{ color: '#777', fontSize: '0.85rem' }}
                          >
                            Project site successfully secured
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            bgcolor: '#e6010815',
                            color: '#e60108',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <ApartmentIcon fontSize="small" />
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#151515',
                              fontSize: '0.95rem',
                            }}
                          >
                            Tower Development
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{ color: '#777', fontSize: '0.85rem' }}
                          >
                            Construction and development planning underway
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    {/* Location */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3.5,
                        p: 1.8,
                        bgcolor: '#f7f7f7',
                        borderRadius: 2,
                        border: '1px solid #eee',
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          bgcolor: '#e6010815',
                          color: '#e60108',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <LocationOnIcon fontSize="small" />
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#888',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            fontSize: '0.65rem',
                          }}
                        >
                          Project Location
                        </Typography>

                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#151515',
                            fontSize: '0.95rem',
                          }}
                        >
                          Logoba, Eswatini
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mt: { xs: 2, md: 'auto' } }}>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        bgcolor: '#e60108',
                        px: 3.5,
                        py: 1.3,
                        borderRadius: 3,
                        fontWeight: 700,
                        '&:hover': {
                          bgcolor: '#c90006',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Project Details
                    </Button>
                  </Box>
                </CardContent>
              </Grid>

            </Grid>
          </Card>
        </Container>
      </Box>

      {/* ================= CLOSING SECTION ================= */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #151515 0%, #2a0a0a 100%)',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 800,
                mb: 2,
                fontSize: {
                  xs: '2rem',
                  md: '3rem',
                },
              }}
            >
              Building Today. Connecting Tomorrow.
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                maxWidth: 700,
                mx: 'auto',
                mb: 4,
              }}
            >
              The Logoba Tower is just the beginning. We are committed to
              expanding our infrastructure footprint and delivering reliable
              connectivity through strategic investments across Eswatini.
            </Typography>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ display: 'inline-block' }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: '#e60108',
                  color: '#fff',
                  px: 5,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: '#c90006',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Get in Touch
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

    </Box>
  );
};

export default Projects;